import type { ComponentProps } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, type UnistylesVariants } from 'react-native-unistyles';
import Box from './Box';
import Icon from './Icon';
import Text from './Text';

type TagProps = UnistylesVariants<typeof style> & {
  label: string;
  icon?: ComponentProps<typeof Icon>['name'];
};

export default function Tag({
  label,
  icon,
  variant,
  size = 'xs',
  style: extraStyles,
}: TagProps & ViewProps) {
  style.useVariants({
    variant,
    size,
  });

  return (
    <Box
      style={[style.container, extraStyles]}
      direction="horizontal"
      space="sm"
    >
      {icon ? <Icon name={icon} variant={variant} size={size} /> : null}
      <Text numberOfLines={1} style={style.text}>
        {label}
      </Text>
    </Box>
  );
}

const style = StyleSheet.create((theme) => ({
  container: {
    maxWidth: theme.size(64),
    overflow: 'hidden',
    borderRadius: theme.borderRadius.xl,
    variants: {
      variant: {
        primary: {
          borderColor: theme.colors.primary,
        },
        secondary: {
          borderColor: theme.colors.secondary,
        },
        success: {
          borderColor: theme.colors.success,
        },
        info: {
          borderColor: theme.colors.info,
        },
        warning: {
          borderColor: theme.colors.warning,
        },
        error: {
          borderColor: theme.colors.error,
        },
      },
      size: {
        xs: {
          paddingHorizontal: theme.size(2),
          paddingVertical: theme.size(1),
          borderWidth: theme.size(0.25),
        },
        sm: {
          paddingHorizontal: theme.size(2),
          paddingVertical: theme.size(1),
          borderWidth: theme.size(0.5),
        },
        md: {
          paddingHorizontal: theme.size(3),
          paddingVertical: theme.size(2),
          borderWidth: theme.size(0.5),
        },
        lg: {
          paddingHorizontal: theme.size(4),
          paddingVertical: theme.size(3),
          borderWidth: theme.size(0.5),
        },
        default: {
          paddingHorizontal: theme.size(1),
          paddingVertical: theme.size(0.5),
          borderWidth: theme.size(0.5),
        },
      },
    },
  },
  icon: {
    variants: {
      variant: {
        primary: {
          color: theme.colors.primary,
        },
        secondary: {
          color: theme.colors.secondary,
        },
        success: {
          color: theme.colors.success,
        },
        info: {
          color: theme.colors.info,
        },
        warning: {
          color: theme.colors.warning,
        },
        error: {
          color: theme.colors.error,
        },
      },
      size: {
        xs: {
          fontSize: theme.fontSize.xs,
        },
        sm: {
          fontSize: theme.fontSize.sm,
        },
        md: {
          fontSize: theme.fontSize.md,
        },
        lg: {
          fontSize: theme.fontSize.lg,
        },
        default: {
          fontSize: theme.fontSize.xs,
        },
      },
    },
  },
  text: {
    textTransform: 'uppercase',
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.bold,
    variants: {
      variant: {
        primary: {
          color: theme.colors.primary,
        },
        secondary: {
          color: theme.colors.secondary,
        },
        success: {
          color: theme.colors.success,
        },
        info: {
          color: theme.colors.info,
        },
        warning: {
          color: theme.colors.warning,
        },
        error: {
          color: theme.colors.error,
        },
      },
      size: {
        xs: {
          fontSize: theme.fontSize.xs,
        },
        sm: {
          fontSize: theme.fontSize.sm,
        },
        md: {
          fontSize: theme.fontSize.md,
        },
        lg: {
          fontSize: theme.fontSize.lg,
        },
        default: {
          fontSize: theme.fontSize.xs,
        },
      },
    },
  },
}));
